import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { FlaskConical, Copy, Check, Eye, EyeOff, RotateCcw, Loader2 } from 'lucide-react';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';

const CopyField = ({ label, value, testId, secret = false }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch { /* clipboard unavailable */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm">
      <div className="min-w-0"><span className="text-gray-500 mr-2">{label}:</span><code className="font-mono text-gray-900 break-all" data-testid={`${testId}-value`}>{value}</code></div>
      <Button variant="ghost" size="sm" onClick={copy} className="ml-3 shrink-0" data-testid={`${testId}-copy`} aria-label={`Copy ${label}`}>
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}<span className="ml-1 text-xs">{secret ? 'Copy Password' : 'Copy Email'}</span>
      </Button>
    </div>
  );
};

export const AdminTestAccounts = () => {
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const { toast } = useToast();

  const reveal = async () => {
    setLoading(true);
    try {
      const r = await apiService.admin.getTestAccounts();
      setAccounts(r.data?.data?.accounts || []);
    } catch (e) {
      toast({ title: 'Could not load test accounts', description: formatApiError(e, 'Administrator authorization required.'), variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const resetPair = async () => {
    setResetMsg('Resetting…');
    try {
      const r = await apiService.admin.resetTestInteractions();
      const d = r.data?.data || {};
      setResetMsg(`Cleared ${d.swipes} swipes, ${d.matches} matches, ${d.messages} messages.`);
    } catch (e) { setResetMsg(formatApiError(e, 'Reset failed.')); }
  };

  return (
    <Card className="p-6 max-w-2xl" data-testid="admin-test-accounts-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold flex items-center"><FlaskConical className="w-5 h-5 mr-2 text-purple-600" />Test Accounts</h2>
        {accounts && <Button variant="outline" size="sm" onClick={() => setAccounts(null)} data-testid="hide-test-credentials-btn"><EyeOff className="w-4 h-4 mr-1" />Hide</Button>}
      </div>
      <p className="text-sm text-gray-600 mb-4">Test credentials are restricted to authorized administrators. They are fetched from the server only when you reveal them and are never stored in the browser.</p>
      {!accounts ? (
        <Button onClick={reveal} disabled={loading} data-testid="reveal-test-credentials-btn" className="bg-purple-600 hover:bg-purple-700 text-white">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}Reveal Test Credentials
        </Button>
      ) : accounts.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="test-accounts-empty">No test accounts are seeded.</p>
      ) : (
        <div className="space-y-5">
          {accounts.map((a) => {
            const key = (a.label || '').split(' ')[0].toLowerCase();
            return (
              <div key={a.email} className="space-y-2" data-testid={`test-account-${key}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{a.note?.split(',')[0] || a.label}</h3>
                  <span className="text-xs text-gray-500">{a.note}</span>
                </div>
                <CopyField label="Email" value={a.email} testId={`${key}-email`} />
                <CopyField label="Password" value={a.password} testId={`${key}-password`} secret />
              </div>
            );
          })}
          <div className="border-t pt-4 space-y-2">
            <Button variant="secondary" onClick={resetPair} data-testid="reset-test-pair-btn"><RotateCcw className="w-4 h-4 mr-2" />Reset swipes/matches between test users</Button>
            {resetMsg && <p className="text-xs text-gray-600" data-testid="reset-test-pair-msg">{resetMsg}</p>}
          </div>
        </div>
      )}
    </Card>
  );
};

export default AdminTestAccounts;
